/** @format */

import { Component, OnInit, EventEmitter, Output, Input, TemplateRef, ViewChild, SimpleChanges, OnChanges } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModalConfig, NgbModal, NgbModalRef, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { NgbInputDatepicker } from '@ng-bootstrap/ng-bootstrap';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';

import { Problem } from '../../models/problem';
import { convertQuillToHTML } from 'src/app/utils/convertQuillToHTML';
import { MenuItem, User as RapUser, ActivityType, WorkOrderTypeList, User } from '../../state-management';

import { Activity, ActivityList } from 'src/app/models/activity';

import { AccountInfo, AccountInfo as MsalUser } from '@azure/msal-browser';
import { NgSelectModule } from '@ng-select/ng-select';

@Component({
  selector: 'app-activity-view',
  templateUrl: './activity.component.html',
  styleUrls: ['./activity.component.scss'],
  providers: [NgbModalConfig, NgbModal],
  standalone: false,
})
export class ActivityComponent implements OnInit, OnChanges {
  @ViewChild('content') content: TemplateRef<any>;

  @Input() menuOption: MenuItem;
  @Input() activities: ActivityList[];
  @Input() authUser: MsalUser;
  @Input() problem: Problem;
  @Input() inProgress = false;
  @Input() inProgressText: string;
  @Input() isCityworksRequest: boolean;
  @Output() SubmitActivity = new EventEmitter<{ workOrderTypeId: number; activity: Activity }>();
  @Output() EditActivity = new EventEmitter<{ workOrderTypeId: number; activityId: number; activity: Activity; cancelWO: boolean }>();
  @Output() CancelActivity = new EventEmitter<ActivityList>();

  today = new Date();
  activityTypeList: ActivityType[];
  quillEditEditor: any;
  quillNewEditor: any;

  form = new UntypedFormGroup({
    activityType: new UntypedFormControl(null),
    assignedTo: new UntypedFormControl(''),
    followupDate: new UntypedFormControl(''),
    initiatedDate: new UntypedFormControl(''),
    notes: new UntypedFormControl(''),
    isComplete: new UntypedFormControl(),
    isCancelled: new UntypedFormControl(),
  });

  editActivityForm: UntypedFormGroup;

  selectedActivity: ActivityList;

  workOrderBtnToggle = false;
  addWorkOrderToggle = false;
  workOrderActivity: WorkOrderTypeList[] = [];
  workOrderTypeRefId: number;
  canWOCancel = false;
  filteredActivities: ActivityType[] = [];

  modal: NgbModalRef = null;
  currentDate: Date = new Date();
  assignees: (
    | {
      user_Id: number;
      name: string;
      email: string;
      employee_No: number;
      group_RefId: number;
      active: boolean;
      upn: string;
      edocsAlias: string;
      type: string;
    }
    | { role_Id: number; group_RefId: number; name: string; email: string; active: boolean; group: string; type: string }
  )[];
  workOrderTypeList: string[];
  workOrderType: string[];
  assignee: RapUser;

  constructor(private modalService: NgbModal, private route: ActivatedRoute, config: NgbModalConfig) {
    config.backdrop = 'static';
  }

  ngOnInit() { }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.menuOption) {
      if (changes.menuOption.currentValue?.allUsersList?.length > 0) {
        const allUsers = this.menuOption.allUsersList.map((usr: User) => {
          return {
            user_Id: usr.user_Id,
            name: usr.firstName + ' ' + usr.lastName,
            email: usr.email,
            employee_No: usr.employee_No,
            group_RefId: usr.group_RefId,
            active: usr.active,
            upn: usr.upn,
            edocsAlias: usr.edocsAlias,
            type: 'Users',
          };
        });
        const roles = this.menuOption.problemRoleList.map((role) => {
          return {
            role_Id: role.role_Id,
            group_RefId: role.group_RefId,
            name: role.name,
            email: role.email,
            active: role.active,
            group: role.group,
            type: 'Roles',
          };
        });
        this.assignees = [...roles, ...allUsers];
      }

      if (changes.menuOption.currentValue?.activityTypeList?.length > 0) {
        this.filteredActivities = this.filterSystemAndActive(this.menuOption.activityTypeList);
      }
    }

    if (this.menuOption.workOrderTypeList.length > 0 && this.problem) {
      this.workOrderTypeList = this.getWorkOrderCategory();
      this.workOrderType = this.getType();
    }
  }

  isSystemActivityOrInactive(name: string) {
    let returnValue = false;
    const actType = this.menuOption.activityTypeList.find((a) => a.activityType_Name === name);
    if (actType) {
      returnValue = actType.systemActivity || !actType.active;
    }

    return returnValue;
  }

  getCityworksUserFromActivity(activity: ActivityList) {
    let note = activity.note;
    let isReassignment = note.includes('Issue reassigned by');

    let splitNote = [];
    splitNote = isReassignment ? note.split('Issue reassigned by') : note.split('Closed by ');
    splitNote = isReassignment ? splitNote[1].split('from') : splitNote[1].split('with the following');
    return splitNote[0].trim();
  }

  getFormattedName(activity: ActivityList) {
    if (activity.createdBy.trim() === 'Cityworks') {
      return `Cityworks - ${this.getCityworksUserFromActivity(activity)}`;
    }

    return activity.createdBy;
  }

  getActivityStatus(activity: ActivityList) {
    if (activity.isCancelled) {
      return 'Cancelled';
    } else {
      switch (activity.activityType) {
        case 'Email':
        case 'Internal Note':
        case 'Letter':
        case 'Meeting':
        case 'Order to Comply':
        case 'Phone Call':
        case 'Reassign':
        case 'Closed':
        case 'In Progress':
        case 'Re-open':
        case 'Code Case Created':
          return 'N/A';

        default:
          return activity.activityDateClosed == null ? 'Open' : 'Complete';
      }
    }
  }

  findUserFromId(userId: number) {
    const user = this.menuOption.allUsersList.find((u) => u.user_Id === userId);
    return user ? `${user.firstName} ${user.lastName}` : '';
  }

  setAssignedTo() {
    const activityType = this.form.get('activityType').value;

    if (activityType && activityType.isAssignable) {
      this.form.get('assignedTo').enable();
      this.form.get('assignedTo').setValidators(Validators.required);

      this.form.get('assignedTo').setValue(
        this.menuOption.allUsersList.find((u) => u.upn.toLowerCase() === this.authUser.username.toLowerCase()) || null // TODO Check this.authUser.userName
      );
    } else {
      this.form.get('assignedTo').disable();
      this.form.get('assignedTo').setValue(null);
    }
  }

  isAssignable() {
    if (this.form.value.activityType) {
      if (!this.form.value.activityType.isAssignable) {
        this.form.get('assignedTo').disable();
        this.form.get('assignedTo').setValue(null);
      }
      return this.form.value.activityType.isAssignable;
    } else {
      return false;
    }
  }

  open(modal: TemplateRef<any>) {
    this.setUpModalForm();
    this.modal = this.modalService.open(modal);
  }

  edit(activity: ActivityList, modal: TemplateRef<any>) {
    this.selectedActivity = activity;

    const tempAssignee = this.menuOption.allUsersList.find((u) => u.firstName + ' ' + u.lastName === activity.assignedTo);
    if (tempAssignee) {
      this.assignee = {
        user_Id: tempAssignee.user_Id,
        name: tempAssignee.firstName + ' ' + tempAssignee.lastName,
        email: tempAssignee.email,
        employee_No: tempAssignee.employee_No,
        group_RefId: tempAssignee.group_RefId,
        active: tempAssignee.active,
        upn: tempAssignee.upn,
        edocsAlias: tempAssignee.edocsAlias,
        type: 'Users',
      } as unknown as User;
    } else {
      const tempRole = this.menuOption.problemRoleList.find((u) => u.name === activity.assignedTo);
      if(tempRole){
        this.assignee = {
            role_Id: tempRole.role_Id,
            group_RefId: tempRole.group_RefId,
            name: tempRole.name,
            email: tempRole.email,
            active: tempRole.active,
            group: tempRole.group,
            type: 'Roles',
        } as unknown as User;
      }
    }


    this.setUpModalForm(activity);

    const activityType = this.form.get('activityType').value;
    if (activityType.isAssignable) {
      this.form.get('assignedTo').enable();
      this.form.get('assignedTo').setValidators(Validators.required);
    }

    this.modal = this.modalService.open(modal);
  }

  cancelActivity(activity: ActivityList) {
    this.CancelActivity.emit(activity);
  }

  setUpModalForm(activity?: ActivityList) {
    // edit activity form route
    if (activity) {
      this.selectedActivity = activity;

      this.form
        .get('activityType')
        .setValue(this.menuOption.activityTypeList.find((u) => u.activityType_Name === activity.activityType) || null);


      // select default value depending if assigned to user or role
      this.form
        .get('assignedTo')
        .setValue(
          this.assignee || 
          null
        );

      if (activity.activityDateClosed !== null) {
        this.form.get('isComplete').setValue(true);
      }

      if (activity.workOrderNo !== null) {
        this.canWOCancel = true;
      } else {
        this.canWOCancel = false;
      }

      if (activity.activityFollowUpdate) {
        const followUpDate = new Date(activity.activityFollowUpdate);
        this.form.get('followupDate').setValue({
          year: followUpDate.getFullYear(),
          month: followUpDate.getMonth() + 1,
          day: followUpDate.getDate(),
        });
      } else {
        this.form.get('followupDate').setValue(null);
      }

      if (activity.activityDate) {
        const activityDate = new Date(activity.activityDate);
        this.form.get('initiatedDate').setValue({
          year: activityDate.getFullYear(),
          month: activityDate.getMonth() + 1,
          day: activityDate.getDate(),
        });
      }

      this.form.get('notes').setValue(activity.note);

      // add activity form route
    } else {
      this.form.get('initiatedDate').setValue({
        year: this.today.getFullYear(),
        month: this.today.getMonth() + 1,
        day: this.today.getDate(),
      });

      this.form.get('followupDate').setValue(null);
    }
  }

  close() {
    if (this.modal) {
      this.modal.close();
      this.form.reset();
      this.addWorkOrderToggle = false;
      this.workOrderBtnToggle = false;
      this.form.removeControl('workOrderCategory');
      this.form.removeControl('workOrderType');
      this.form.removeControl('workOrderDescription');
    }
  }

  dismiss() {
    if (this.modal) {
      this.modal.dismiss();
      this.form.reset();
      this.addWorkOrderToggle = false;
      this.workOrderBtnToggle = false;
      this.form.removeControl('workOrderCategory');
      this.form.removeControl('workOrderType');
      this.form.removeControl('workOrderDescription');
    }
  }

  onSubmit(formType: string) {
    // this.submitActivity = { problemNo: null, newActivity: null };
    const userOrRole: any = this.form.get('assignedTo').value;

    const activity: Activity = {
      problem_RefId: this.problem.problemId,
      user_RefId: userOrRole && userOrRole.user_Id !== undefined ? userOrRole.user_Id : null,
      role_RefId: userOrRole && userOrRole.role_Id !== undefined ? userOrRole.role_Id : null,
      activityType_RefId: this.form.value.activityType.activityType_Id,
      activityDate: this.form.value.initiatedDate
        ? new Date(this.form.value.initiatedDate.year, this.form.value.initiatedDate.month - 1, this.form.value.initiatedDate.day)
        : new Date(),
      activityDateClosed:
        this.form.value.isComplete === true ? new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate()) : null,
      activityFollowUpdate: this.form.value.followupDate
        ? new Date(this.form.value.followupDate.year, this.form.value.followupDate.month - 1, this.form.value.followupDate.day)
        : null,
      // this.form.value.followupDate,
      // note: this.form.value.notes ? this.form.value.notes : null,
      createdBy: null, // This will be populated server side
    } as Activity;

    if (formType === 'new') {
      activity.note = this.form.value.notes
        ? convertQuillToHTML(this.quillNewEditor.root?.innerHTML, this.quillNewEditor.getText().trim())
        : null;

      if (this.addWorkOrderToggle === true) {
        this.SubmitActivity.emit({ workOrderTypeId: this.form.value.workOrderDescription.workOrderType_Id, activity });
      } else {
        this.SubmitActivity.emit({ workOrderTypeId: 0, activity });
      }
    } else if (formType === 'edit') {
      activity.note = this.form.value.notes
        ? convertQuillToHTML(this.quillEditEditor.root?.innerHTML, this.quillEditEditor.getText().trim())
        : null;

      if (this.addWorkOrderToggle === true) {
        this.EditActivity.emit({
          workOrderTypeId: this.form.value.workOrderDescription.workOrderType_Id,
          activityId: this.selectedActivity.activity_Id,
          activity: activity,
          cancelWO: this.form.value.isCancelled,
        });
      } else {
        this.EditActivity.emit({
          workOrderTypeId: 0,
          activityId: this.selectedActivity.activity_Id,
          activity: activity,
          cancelWO: this.form.value.isCancelled,
        });
      }
    }

    this.close();
  }

  change(el: HTMLOrSVGElement, dael: NgbInputDatepicker) {
    dael.open();
    setTimeout(function () {
      el.focus();
    }, 500);
  }

  dateInFuture(aDate: Date) {
    let isFuture = false;
    if (aDate) {
      isFuture = Date.parse(aDate.toString()) > this.currentDate.getTime();
    }
    return isFuture;
  }

  filterSystemAndActive(list: ActivityType[]) {
    return list.filter((l) => l.systemActivity === false && l.active === true);
  }

  filterActiveUser(users: RapUser[]) {
    return users.filter((u) => u.active);
  }

  onNewEditorCreated(e: any) {
    this.quillNewEditor = e;
  }

  onEditEditorCreated(e: any) {
    this.quillEditEditor = e;
    const note = this.form.get('notes').value;
    // use {text: note} to show html
    const delta = this.quillEditEditor.clipboard.convert({ text: note });
    this.quillEditEditor.setContents(delta, 'silent');
  }

  // WorkOrder
  toggleWorkOrder(evt: ActivityType) {
    this.setAssignedTo();

    const workOrders = this.menuOption.workOrderTypeList.filter((wo) => wo.problemType_RefId === this.problem.problemType_RefId);

    if (evt?.activityType_Name === 'Work Request' && workOrders.length > 0) {
      this.workOrderBtnToggle = true;
    } else if (evt?.activityType_Name === 'Work Request' && this.problem.group_RefId === 12) {
      this.workOrderBtnToggle = true;
    } else {
      this.triggerWorkOrderMenu(); // remove
      this.workOrderBtnToggle = false;
      this.addWorkOrderToggle = false;
    }
  }

  // WorkOrder
  triggerWorkOrderMenu() {
    if (!this.addWorkOrderToggle) {
      this.form.addControl('workOrderCategory', new UntypedFormControl(''));
      this.form.addControl('workOrderType', new UntypedFormControl(''));
      this.form.addControl('workOrderDescription', new UntypedFormControl(''));

      this.workOrderActivity = this.menuOption.workOrderTypeList.filter((wo) => wo.problemType_RefId === this.problem.problemType_RefId);
    } else {
      this.form.removeControl('workOrderCategory');
      this.form.removeControl('workOrderType');
      this.form.removeControl('workOrderDescription');
      this.workOrderActivity = [];
    }
    this.addWorkOrderToggle = !this.addWorkOrderToggle;
  }

  // WorkOrder
  getWorkOrderCategory() {
    const category = this.menuOption.workOrderTypeList
      .filter((wo) => wo.group_RefId === this.problem.group_RefId)
      .map((wo) => wo.workOrderCategory)
      .filter((value, index, self) => self.indexOf(value) === index);
    return category;
  }

  getType() {
    const type = this.menuOption.workOrderTypeList
      .filter((wo) => wo.group_RefId === this.problem.group_RefId)
      .map((wo) => wo.type)
      .filter((value, index, self) => self.indexOf(value) === index);
    return type;
  }

  // WorkOrder
  setWorkOrderCategory() {
    this.setWorkOrderActivityList();
  }

  setWorkOrderType() {
    this.setWorkOrderActivityList();
  }

  setWorkOrderActivityList() {
    if (this.form.value.workOrderType && this.form.value.workOrderCategory) {
      this.form.get('workOrderDescription').setValue('');
      this.workOrderActivity = this.menuOption.workOrderTypeList.filter(
        (wo) => wo.type === this.form.value.workOrderType && wo.workOrderCategory === this.form.value.workOrderCategory
      );
    }
  }
}
